import type {
  Ref,
} from 'vue';
import {
  computed,
  ref,
} from 'vue';

import type {
  MenuItem,
  PrimeVueMenuItem,
} from '../types';

interface MenuItemInner extends MenuItem {
  hasPermission?: Ref<boolean>;
}

function prepareMenuItems(
  items: PrimeVueMenuItem[],
  level = 0,
): PrimeVueMenuItem[] {
  return items?.map((item) => ({
    ...item,
    key: `${String(item.id)}-${Math.random()}`,
    route: item.to,
    label: item.caption ? item.caption : item.label,
    level,
    items: item.items ? prepareMenuItems(item.items, level + 1) : undefined,
  })) || [];
}

export default class MenuConstructor {
  private items: Ref<MenuItemInner[]> = ref([]);

  private finalItems: Ref<PrimeVueMenuItem[]> | undefined;

  private sortItems(items: PrimeVueMenuItem[]): void {
    items.sort((a, b) => (
      Number(a.order) - Number(b.order)
    ));
  }

  private findItem(items: PrimeVueMenuItem[], id: MenuItem['id']): PrimeVueMenuItem | undefined {
    for (const item of items) {
      if (item.id === id) {
        return item;
      }

      if (item.items) {
        const childItem = this.findItem(item.items, id);
        if (childItem) {
          return childItem;
        }
      }
    }

    return undefined;
  }

  addItem(item: MenuItem): MenuConstructor {
    const {
      parentItem,
      ...menuItem
    } = item;

    if (!parentItem) {
      this.items.value.push(menuItem);
      this.sortItems(this.items.value);
      return this;
    }

    const parent = this.findItem(this.items.value, parentItem);
    if (!parent) {
      throw new Error(`Parent item with id ${String(parentItem)} not found`);
    }

    if (!parent.items) {
      parent.items = [];
    }

    parent.items.push(menuItem);
    this.sortItems(parent.items);

    return this;
  }

  addChildItem(parentItem: MenuItem['id'], item: MenuItem): MenuConstructor {
    return this.addItem({
      ...item,
      parentItem,
    });
  }

  getItems(): Ref<PrimeVueMenuItem[]> {
    if (!this.finalItems) {
      this.finalItems = computed<PrimeVueMenuItem[]>(() => prepareMenuItems(this.items.value));
    }

    return this.finalItems;
  }
}
